// WACheckin Pay: each OWNER of the apartments a manager runs gets paid into
// their own account, opened by the platform. See es/views/wacheckinPay.js.
export const wacheckinPay = {
  modes: {
    own: {
      title: "My Stripe accounts",
      text: "Connect your own Stripe account. Best if you or your company get paid.",
    },
    pay: {
      badge: "NEW",
      text: "For managers: each owner gets their own payouts. You just send them a link.",
    },
  },
  ownerAccountName: "Owner · {name}",

  hero: {
    title: "Payments for your owners",
    text: "Turn on payments for each apartment in its owner's name. Booking money goes straight to their bank, without them having to know what Stripe is.",
    step1: "Choose an owner's apartments",
    step2: "Enter their name and email",
    step3: "They get a link, add their IBAN and start getting paid",
  },
  stats: {
    owners: "Owners",
    ready: "Getting paid",
    pending: "Pending",
    withoutOwner: "Apartments without owner",
  },

  suggest: {
    title: "Your owners without payments",
    subtitle: "They're already in your team or Owners list. Activate their payments with their details and apartments pre-filled.",
    apartments: "{count} apartment | {count} apartments",
  },

  owners: {
    title: "Owners",
    subtitle: "Each owner has a single account for all their apartments.",
    new: "New owner",
    emptyTitle: "No owners yet",
    emptyText: "Pick an owner's apartments below and click “Activate payments”. They'll get a link to add their details and bank account.",
    sentAt: "link sent on {date}",
    addApartments: "Add apartments",
    hasAccess: "Panel access",
    removeApartment: "Remove this apartment",
  },

  status: {
    pendiente: "Waiting for owner",
    faltan_datos: "Details missing",
    en_revision: "Under review",
    lista: "Getting paid",
    bloqueada: "Rejected",
  },
  statusHint: {
    pendiente: "They haven't completed their details yet. Send them the link.",
    faltan_datos: "Stripe needs more information. Send them the link again so they can finish.",
    en_revision: "They've sent their details and Stripe is reviewing them. This usually takes a few minutes.",
    bloqueada: "Stripe couldn't verify this account. Move their apartments to another account.",
  },

  actions: {
    copyLink: "Copy link",
    copy: "Copy",
    sendEmail: "Send by email",
    resendEmail: "Resend email",
    sendWhatsapp: "Send via WhatsApp",
    refresh: "Check status",
    edit: "Edit owner",
    delete: "Delete owner",
  },

  apartments: {
    title: "Apartments",
    subtitle: "Choose who gets paid for each apartment. You can select several at once.",
    search: "Search apartment…",
    filter: {
      all: "All",
      without: "Without owner",
      with: "With owner",
      pending: "Owner pending",
    },
    colApartment: "Apartment",
    colCollects: "Gets paid",
    activate: "Activate payments",
    change: "Change",
    activateSelected: "Activate payments for an owner",
    assignToExisting: "Move to an owner…",
    selected: "{count} apartment selected | {count} apartments selected",
    clear: "Clear selection",
    ownAccount: "Your account · {name}",
    ownerNotActive: "Owner: {name} (not active)",
    noAccount: "No payment account",
    noMatch: "No apartment matches your search.",
    none: "You have no apartments that take payments with Stripe.",
  },

  wizard: {
    title: "Activate payments",
    doneTitle: "Owner created!",
    stepApartments: "Apartments",
    stepOwner: "Owner",
    stepLink: "Link",
    apartmentsLead: "Which apartments belong to this owner? They'll all be paid into the same account.",
    movesFrom: "currently {name}",
    ownerLead: "Owner details for {count} apartment. | Owner details for {count} apartments.",
    create: "Create and generate link",
    doneEmailSent: "We've emailed {name} at {email} with their link. You can also send it via WhatsApp.",
    doneNoEmail: "Send this link to {name} so they can add their details and bank account.",
    linkLabel: "Owner link",
    linkHint: "It doesn't expire: if they stop halfway, they can open it again and continue.",
  },

  form: {
    giveAccess: "Give them panel access as Owner",
    giveAccessHint: "They're added to Team management with the Owner role: they'll see the calendar and analytics of their apartments. Same person, not a separate user.",
    alreadyMember: "{name} is already in your team as Owner: these apartments will be added.",
    password: "Password",
    passwordConfirm: "Repeat password",
    passwordShort: "The password needs at least 8 characters.",
    passwordMismatch: "Passwords don't match.",
    fromList: "Owner from your team or list",
    fromListNone: "— Another owner (enter their details) —",
    alreadyActive: "already active",
    name: "Owner or company name",
    namePh: "e.g. John Smith or Sun Investments Ltd",
    email: "Email",
    phone: "Phone (WhatsApp)",
    country: "Country of their bank",
    countryHint: "Stripe doesn't allow changing it later.",
    lang: "Email language",
    sendEmail: "Email the link to the owner now",
    privacy: "The owner enters their personal and bank details directly in Stripe. Neither you nor WACheckin can see them.",
    nameRequired: "Enter the owner's name.",
    emailRequired: "Enter a valid email.",
  },

  add: {
    title: "Add apartments to {name}",
    save: "Add",
  },
  move: {
    title: "Who gets paid for “{apt}” now?",
    lead: "{name} will stop getting paid for it. Choose which account takes payments from now on.",
    target: "Gets paid into",
    toDefault: "Your main account · {name}",
    toNone: "No account (can't take payments)",
    ownGroup: "Your other accounts",
    ownersGroup: "Another owner",
    noDefaultWarn: "You have no main Stripe account: this apartment can't take payments until you assign it.",
    save: "Save",
  },
  edit: {
    title: "Edit owner",
    note: "Change contact details. Bank details can only be changed by the owner in Stripe.",
    save: "Save",
  },
  delete: {
    title: "Delete {name}?",
    lead: "Their account will no longer be used for payments. Past payments are not affected.",
    moveTo: "Their apartment will be paid into: | Their {count} apartments will be paid into:",
    confirm: "Delete",
  },

  msg: {
    copied: "Link copied",
    emailSent: "Email sent to {email}",
    saved: "Saved",
    deleted: "Owner deleted",
    error: "Couldn't complete that. Please try again.",
    createError: "Couldn't create the owner. Please try again.",
  },

  alerts: {
    title: "There is {count} problem with an owner's money | There are {count} problems with your owners' money",
  },
  activity: {
    button: "Activity",
    title: "{name}'s activity",
    loading: "Checking with Stripe…",
    stripeDown: "Stripe didn't respond right now. Saved alerts are shown; try again in a moment.",
    available: "Available",
    pending: "Pending",
    bankPayouts: "Payouts to their bank",
    enabled: "Enabled",
    disabled: "Blocked",
    tabPayouts: "Bank payouts",
    tabPayments: "Payments",
    tabEvents: "Alerts",
    arrivedOn: "Arrived on {date}",
    expectedOn: "Expected on {date}",
    sentOn: "From {date}",
    payoutStatus: {
      paid: "Paid",
      in_transit: "In transit",
      pending: "Pending",
      failed: "Failed",
      canceled: "Canceled",
    },
    guest: "Guest",
    booking: "booking #{id}",
    paid: "Paid",
    refunded: "Refunded",
    partialRefund: "Refund of {amount}",
    disputeLine: "Dispute of {amount}: {reason}",
    disputeStatus: {
      warning_needs_response: "Dispute: response needed",
      warning_under_review: "Dispute under review",
      warning_closed: "Dispute closed",
      needs_response: "Dispute: response needed",
      under_review: "Dispute under review",
      won: "Dispute won",
      lost: "Dispute lost",
      prevented: "Dispute prevented",
    },
    problem: "Check",
    ok: "OK",
    noPayouts: "No payouts to their bank yet.",
    noPayments: "No payments in this account yet.",
    noEvents: "No alerts. You'll see here if a payout fails or a guest opens a dispute.",
    ownerDashboard: "Owner's Stripe dashboard",
    reload: "Refresh",
  },
  events: {
    payoutFailed: "Payout of {amount} failed: {reason}",
    payoutCanceled: "Payout of {amount} canceled",
    payoutPaid: "Payout of {amount} paid to their bank",
    refunded: "Refund of {amount} to a guest",
    disputeOpened: "Dispute opened for {amount}: {reason}",
    disputeWon: "Dispute of {amount} won",
    disputeLost: "Dispute of {amount} lost",
  },
  failure: {
    generic: "the bank rejected it",
    account_closed: "the bank account is closed",
    account_frozen: "the bank account is frozen",
    bank_account_restricted: "the bank account is restricted",
    bank_ownership_changed: "the bank account changed owner",
    could_not_process: "the bank couldn't process it",
    debit_not_authorized: "the bank didn't authorize it",
    declined: "the bank declined it",
    insufficient_funds: "not enough balance in Stripe",
    invalid_account_number: "the IBAN is invalid",
    incorrect_account_holder_name: "the holder name doesn't match the account",
    invalid_currency: "the bank doesn't accept that currency",
    no_account: "that bank account doesn't exist",
    unsupported_card: "the destination card isn't supported",
  },
  disputeReason: {
    general: "general reason",
    fraudulent: "the cardholder says they didn't make it",
    duplicate: "duplicate charge",
    product_not_received: "says the service wasn't received",
    product_unacceptable: "says the service wasn't as agreed",
    subscription_canceled: "says they had canceled",
    credit_not_processed: "says they weren't refunded",
    unrecognized: "doesn't recognize the charge",
  },

  whatsappText: "Hi {name} 👋 To receive your apartments' booking payments straight into your bank, complete your details here (5 minutes): {url}",

  next: "Next",
  back: "Back",
  done: "Done",
  cancel: "Cancel",
  close: "Close",
};
